export type ApplicationStatus = {
  
  id: number;
  descriptionKg: string;
  textColor: string;
  backgroundColor: string;
  name: string;
  description: string;
  code: string;
  nameKg: string;
};


export type ApplicationStatusCreateModel = {
  
  id: number;
  descriptionKg: string;
  textColor: string;
  backgroundColor: string;
  name: string;
  description: string;
  code: string;
  nameKg: string;
};

export const APPLICATION_CABINET_STATUSES = {
  draft: 'draft',
  under_consideration: 'under_consideration',
  rejected: 'rejected',
  accepted: 'accepted',
  return_with_error: 'return_with_error',
  done: 'done',
  documents_ready: 'documents_ready',
  denied: 'denied',
  payment_required: 'payment_required',
  signature_required: 'signature_required',
  /**
   * Самоотказ: заявитель отозвал отправленную заявку.
   * Локальный статус кабинета — портал его не присылает (у портального `sam_otkaz`
   * `cabinet_status_code` = NULL), ставит его сам кабинет при успешном отзыве.
   * См. docs/contracts/application-withdrawal-contract.md §2.
   */
  self_withdrawal: 'self_withdrawal',
} as const;

export type ApplicationCabinetStatusCode =
  typeof APPLICATION_CABINET_STATUSES[keyof typeof APPLICATION_CABINET_STATUSES];

export type ApplicationCabinetStatusCategory = 'draft' | 'active' | 'terminal' | 'legacy';

export const APPLICATION_CABINET_STATUS_META: Record<
  ApplicationCabinetStatusCode,
  { labelKey: string; category: ApplicationCabinetStatusCategory }
> = {
  [APPLICATION_CABINET_STATUSES.draft]: {
    labelKey: 'label:applicationHeader.status.draft',
    category: 'draft',
  },
  [APPLICATION_CABINET_STATUSES.under_consideration]: {
    labelKey: 'label:applicationHeader.status.underConsideration',
    category: 'active',
  },
  [APPLICATION_CABINET_STATUSES.rejected]: {
    labelKey: 'label:applicationHeader.status.rejected',
    category: 'legacy',
  },
  [APPLICATION_CABINET_STATUSES.accepted]: {
    labelKey: 'label:applicationHeader.status.accepted',
    category: 'active',
  },
  [APPLICATION_CABINET_STATUSES.return_with_error]: {
    labelKey: 'label:applicationHeader.status.returnWithError',
    category: 'active',
  },
  [APPLICATION_CABINET_STATUSES.done]: {
    labelKey: 'label:applicationHeader.status.done',
    category: 'terminal',
  },
  [APPLICATION_CABINET_STATUSES.documents_ready]: {
    labelKey: 'label:applicationHeader.status.documentsReady',
    category: 'terminal',
  },
  [APPLICATION_CABINET_STATUSES.denied]: {
    labelKey: 'label:applicationHeader.status.denied',
    category: 'terminal',
  },
  [APPLICATION_CABINET_STATUSES.payment_required]: {
    labelKey: 'label:applicationHeader.status.paymentRequired',
    category: 'active',
  },
  [APPLICATION_CABINET_STATUSES.signature_required]: {
    labelKey: 'label:applicationHeader.status.signatureRequired',
    category: 'active',
  },
  [APPLICATION_CABINET_STATUSES.self_withdrawal]: {
    labelKey: 'label:applicationHeader.status.selfWithdrawal',
    category: 'terminal',
  },
};

export const APPLICATION_CABINET_STATUS_GROUPS = {
  terminal: [
    APPLICATION_CABINET_STATUSES.done,
    APPLICATION_CABINET_STATUSES.documents_ready,
    APPLICATION_CABINET_STATUSES.self_withdrawal,
  ],
  requiresAction: [
    APPLICATION_CABINET_STATUSES.return_with_error,
    APPLICATION_CABINET_STATUSES.signature_required,
    APPLICATION_CABINET_STATUSES.payment_required,
  ],
  readOnly: [
    APPLICATION_CABINET_STATUSES.under_consideration,
    APPLICATION_CABINET_STATUSES.accepted,
    // Отозванная заявка редактированию не подлежит: на портале она уже в sam_otkaz.
    APPLICATION_CABINET_STATUSES.self_withdrawal,
  ],
  contractAvailable: [
    APPLICATION_CABINET_STATUSES.signature_required,
    APPLICATION_CABINET_STATUSES.payment_required,
    APPLICATION_CABINET_STATUSES.done,
    APPLICATION_CABINET_STATUSES.documents_ready,
  ],
} as const;

export const APPLICATION_PORTAL_STATUSES = {
  approved_cabinet: 'approved_cabinet',
  document_ready: 'document_ready',
  document_issued: 'document_issued',
  document_issued_free: 'document_issued_free',
  done: 'done',
  refusal_ready: 'refusal_ready',
  refusal_sent: 'refusal_sent',
  refusal_issued: 'refusal_issued',
} as const;

export const APPLICATION_PORTAL_STATUS_GROUPS = {
  terminal: [
    APPLICATION_PORTAL_STATUSES.document_issued,
    APPLICATION_PORTAL_STATUSES.document_issued_free,
    APPLICATION_PORTAL_STATUSES.done,
  ],
  refused: [APPLICATION_PORTAL_STATUSES.refusal_issued],
} as const;

export const getPortalApplicationProgressState = (statusCode: string) => {
  if ((APPLICATION_PORTAL_STATUS_GROUPS.terminal as readonly string[]).includes(statusCode)) {
    return 'terminal';
  }

  if ((APPLICATION_PORTAL_STATUS_GROUPS.refused as readonly string[]).includes(statusCode)) {
    return 'refused';
  }

  return 'active';
};
